import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Eye, EyeOff, Plus, Trash2, Key, Activity, Send, Clock, ChevronDown, ChevronUp, Copy, Check, AlertTriangle, Download, RefreshCw, FileText } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Wallet } from 'lucide-react';
import AppBalances from './AppBalances';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const colors = {
        completed: 'bg-green-500/20 text-green-400',
        pending: 'bg-amber-500/20 text-amber-400',
        failed: 'bg-red-500/20 text-red-400',
        cancelled: 'bg-slate-500/20 text-slate-400',
    };
    return <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${colors[status] || colors.pending}`}>{status}</span>;
};

const ProviderBadge = ({ provider }) => (
    <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${
        provider === 'wave' ? 'bg-blue-500/20 text-blue-400'
        : provider === 'proxy' ? 'bg-emerald-500/20 text-emerald-400'
        : 'bg-orange-500/20 text-orange-400'
    }`}>
        {provider === 'wave' ? 'Wave' : provider === 'proxy' ? 'App (proxy)' : 'Orange Money'}
    </span>
);

const PaymentsSection = () => {
    const { t } = useLanguage();
    const [tab, setTab] = useState('history');
    const [payments, setPayments] = useState([]);
    const [stats, setStats] = useState({ per_app: {}, grand_total: { wave: 0, orange_money: 0 } });
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('dkr-token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const fetchData = useCallback(async () => {
        try {
            const [h, s, k] = await Promise.all([
                axios.get(`${API}/payments/history`, auth),
                axios.get(`${API}/payments/stats`, auth),
                axios.get(`${API}/payments/api-keys`, auth),
            ]);
            setPayments(h.data);
            setStats(s.data);
            setApiKeys(k.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

    return (
        <div data-testid="admin-payments">
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <CreditCard className="text-blue-400" /> {t('Paiements', 'Payments')}
                </h2>
            </div>

            {/* Summary cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="glass-card p-5">
                    <p className="text-sm text-slate-400 mb-1">Wave</p>
                    <p className="text-2xl font-bold text-blue-400">{stats.grand_total.wave.toLocaleString()} XOF</p>
                    <p className="text-xs text-slate-500 mt-1">{t('Total encaiss\u00e9', 'Total collected')}</p>
                </div>
                <div className="glass-card p-5">
                    <p className="text-sm text-slate-400 mb-1">Orange Money</p>
                    <p className="text-2xl font-bold text-orange-400">{stats.grand_total.orange_money.toLocaleString()} XOF</p>
                    <p className="text-xs text-slate-500 mt-1">{t('Total encaiss\u00e9', 'Total collected')}</p>
                </div>
                <div className="glass-card p-5">
                    <p className="text-sm text-slate-400 mb-1">Total</p>
                    <p className="text-2xl font-bold text-white">
                        {(stats.grand_total.wave + stats.grand_total.orange_money).toLocaleString()} XOF
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{payments.length} {t('transactions', 'transactions')}</p>
                </div>
            </div>

            {/* Per-app stats */}
            {Object.keys(stats.per_app).length > 0 && (
                <div className="glass-card p-5 mb-8">
                    <h3 className="text-sm font-bold text-white mb-3">{t('Par application', 'Per application')}</h3>
                    <div className="space-y-2">
                        {Object.entries(stats.per_app).map(([app, data]) => (
                            <div key={app} className="flex items-center justify-between py-2 border-b border-white/5">
                                <span className="text-sm text-white font-medium">{app}</span>
                                <div className="flex gap-4">
                                    <span className="text-xs text-blue-400">Wave: {data.wave.completed.toLocaleString()} XOF ({data.wave.count})</span>
                                    <span className="text-xs text-orange-400">OM: {data.orange_money.completed.toLocaleString()} XOF ({data.orange_money.count})</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-slate-800 mb-6">
                    <TabsTrigger value="history" className="data-[state=active]:bg-blue-600"><Clock size={14} className="mr-1" />{t('Historique', 'History')}</TabsTrigger>
                    <TabsTrigger value="balances" className="data-[state=active]:bg-blue-600" data-testid="balances-tab"><Wallet size={14} className="mr-1" />{t('Soldes', 'Balances')}</TabsTrigger>
                    <TabsTrigger value="keys" className="data-[state=active]:bg-blue-600"><Key size={14} className="mr-1" />{t('Cl\u00e9s API', 'API Keys')}</TabsTrigger>
                    <TabsTrigger value="docs" className="data-[state=active]:bg-blue-600"><Activity size={14} className="mr-1" />API Docs</TabsTrigger>
                    <TabsTrigger value="test" className="data-[state=active]:bg-blue-600"><Send size={14} className="mr-1" />Test API</TabsTrigger>
                </TabsList>

                <TabsContent value="history"><PaymentHistory payments={payments} /></TabsContent>
                <TabsContent value="balances"><AppBalances auth={auth} /></TabsContent>
                <TabsContent value="keys"><ApiKeysManager apiKeys={apiKeys} auth={auth} onRefresh={fetchData} /></TabsContent>
                <TabsContent value="docs"><ApiDocs /></TabsContent>
                <TabsContent value="test"><ApiTestSandbox auth={auth} /></TabsContent>
            </Tabs>
        </div>
    );
};

// ==================== PAYMENT HISTORY ====================
const PaymentHistory = ({ payments }) => {
    const { t } = useLanguage();
    const [filter, setFilter] = useState('all');

    const filtered = filter === 'all' ? payments : payments.filter(p => p.provider === filter);

    return (
        <div>
            <div className="flex gap-2 mb-4">
                {['all', 'wave', 'orange_money'].map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${filter === f ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                        {f === 'all' ? t('Tous', 'All') : f === 'wave' ? 'Wave' : 'Orange Money'}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="glass-card p-12 text-center">
                    <CreditCard size={40} className="mx-auto text-slate-500 mb-3" />
                    <p className="text-slate-400">{t('Aucun paiement enregistr\u00e9', 'No payments recorded')}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(p => (
                        <div key={p.id} className="glass-card p-4 flex items-center justify-between" data-testid={`payment-${p.id}`}>
                            <div className="flex items-center gap-3 flex-1">
                                <ProviderBadge provider={p.provider} />
                                <span className="text-white text-sm font-medium">{p.app_name}</span>
                                <StatusBadge status={p.status} />
                            </div>
                            <div className="text-right">
                                <p className="text-white font-bold">{p.amount.toLocaleString()} {p.currency}</p>
                                <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString()}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==================== API KEYS MANAGER ====================
const ApiKeysManager = ({ apiKeys, auth, onRefresh }) => {
    const { t } = useLanguage();
    const [addOpen, setAddOpen] = useState(false);
    const [newKey, setNewKey] = useState({ provider: 'wave', key_name: '', value: '' });
    const [appOpen, setAppOpen] = useState(false);
    const [appName, setAppName] = useState('');
    const [generated, setGenerated] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [revealedKeys, setRevealedKeys] = useState({});
    const [passwordDialog, setPasswordDialog] = useState({ open: false, keyId: null });
    const [password, setPassword] = useState('');

    const handleAdd = async () => {
        if (!newKey.key_name || !newKey.value) return;
        try {
            await axios.post(`${API}/payments/api-keys`, newKey, auth);
            toast.success(t('Cl\u00e9 ajout\u00e9e', 'Key added'));
            setAddOpen(false);
            setNewKey({ provider: 'wave', key_name: '', value: '' });
            onRefresh();
        } catch (e) { toast.error(t('Erreur', 'Error')); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Supprimer cette cl\u00e9 ?', 'Delete this key?'))) return;
        try {
            await axios.delete(`${API}/payments/api-keys/${id}`, auth);
            toast.success(t('Cl\u00e9 supprim\u00e9e', 'Key deleted'));
            onRefresh();
        } catch (e) { toast.error(t('Erreur', 'Error')); }
    };

    const handleReveal = async () => {
        try {
            const res = await axios.post(`${API}/payments/api-keys/${passwordDialog.keyId}/reveal`, { password }, auth);
            setRevealedKeys(prev => ({ ...prev, [passwordDialog.keyId]: res.data.value }));
            setPasswordDialog({ open: false, keyId: null });
            setPassword('');
            toast.success(t('Cl\u00e9 r\u00e9v\u00e9l\u00e9e', 'Key revealed'));
        } catch (e) {
            toast.error(t('Mot de passe incorrect', 'Wrong password'));
        }
    };

    const handleGenerateApp = async () => {
        if (!appName.trim()) { toast.error(t('Nom d\'app requis', 'App name required')); return; }
        setGenerating(true);
        try {
            const res = await axios.post(`${API}/payments/api-keys/generate-app`, { app_name: appName }, auth);
            setGenerated(res.data);
            setAppName('');
            toast.success(t(`Cl\u00e9 g\u00e9n\u00e9r\u00e9e pour ${res.data.app_name}`, `Key generated for ${res.data.app_name}`));
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('Erreur de g\u00e9n\u00e9ration', 'Generation failed'));
        } finally { setGenerating(false); }
    };

    const handleRotate = async (id, name) => {
        if (!window.confirm(t(`R\u00e9g\u00e9n\u00e9rer la cl\u00e9 de \u00ab ${name} \u00bb ? L'ancienne cl\u00e9 sera imm\u00e9diatement invalid\u00e9e : l'app devra utiliser la nouvelle.`, `Regenerate the key for "${name}"? The old key will be invalidated immediately.`))) return;
        try {
            const res = await axios.post(`${API}/payments/api-keys/${id}/rotate`, {}, auth);
            setGenerated(res.data);
            setAppOpen(true);
            toast.success(t(`Nouvelle cl\u00e9 g\u00e9n\u00e9r\u00e9e pour ${res.data.app_name}`, `New key generated for ${res.data.app_name}`));
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || t('Erreur de rotation', 'Rotation failed')); }
    };

    const downloadIntegrationFile = async (keyId) => {
        try {
            const res = await axios.get(`${API}/payments/api-keys/${keyId}/integration-file`, auth);
            const blob = new Blob([res.data.content], { type: 'text/markdown;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res.data.filename;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(t('Fichier d\'int\u00e9gration t\u00e9l\u00e9charg\u00e9', 'Integration file downloaded'));
        } catch (e) { toast.error(e.response?.data?.detail || t('Erreur de t\u00e9l\u00e9chargement', 'Download failed')); }
    };

    const copyKey = (value) => {
        navigator.clipboard.writeText(value);
        toast.success(t('Copi\u00e9', 'Copied'));
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-400" />
                    <p className="text-xs text-slate-400">{t('Les cl\u00e9s sont chiffr\u00e9es. Le mot de passe admin est requis pour les r\u00e9v\u00e9ler.', 'Keys are encrypted. Admin password required to reveal.')}</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => { setGenerated(null); setAppOpen(true); }} className="bg-emerald-600 hover:bg-emerald-500" data-testid="create-app-btn">
                        <Plus className="w-4 h-4 mr-1" />{t('Cr\u00e9er une app', 'Create app')}
                    </Button>
                    <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="add-key-btn">
                        <Plus className="w-4 h-4 mr-1" />{t('Ajouter', 'Add')}
                    </Button>
                </div>
            </div>

            <div className="space-y-2">
                {apiKeys.length === 0 ? (
                    <div className="glass-card p-8 text-center">
                        <Key size={32} className="mx-auto text-slate-500 mb-2" />
                        <p className="text-slate-400 text-sm">{t('Aucune cl\u00e9 API configur\u00e9e', 'No API keys configured')}</p>
                        <p className="text-slate-500 text-xs mt-1">{t('Ajoutez vos cl\u00e9s Wave et Orange Money pour activer les paiements.', 'Add your Wave and Orange Money keys to enable payments.')}</p>
                    </div>
                ) : apiKeys.map(k => (
                    <div key={k.id} className="glass-card p-4 flex items-center justify-between" data-testid={`key-${k.id}`}>
                        <div className="flex items-center gap-3">
                            <ProviderBadge provider={k.provider} />
                            <span className="text-white text-sm font-bold">{k.key_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {revealedKeys[k.id] ? (
                                <>
                                    <code className="text-xs text-green-400 bg-slate-800 px-2 py-1 rounded font-mono max-w-xs truncate">{revealedKeys[k.id]}</code>
                                    <Button size="sm" variant="ghost" onClick={() => copyKey(revealedKeys[k.id])}><Copy size={14} /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => setRevealedKeys(prev => { const n = {...prev}; delete n[k.id]; return n; })}><EyeOff size={14} /></Button>
                                </>
                            ) : (
                                <>
                                    <code className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded">***</code>
                                    <Button size="sm" variant="ghost" className="text-blue-400" onClick={() => setPasswordDialog({ open: true, keyId: k.id })} data-testid={`reveal-${k.id}`}>
                                        <Eye size={14} />
                                    </Button>
                                </>
                            )}
                            {k.provider === 'proxy' && (
                                <>
                                    <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300" title={t('Fichier d\'int\u00e9gration (prompt)', 'Integration file (prompt)')}
                                        onClick={() => downloadIntegrationFile(k.id)} data-testid={`integration-file-${k.id}`}>
                                        <FileText size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300" title={t('R\u00e9g\u00e9n\u00e9rer la cl\u00e9', 'Rotate key')}
                                        onClick={() => handleRotate(k.id, k.key_name)} data-testid={`rotate-key-${k.id}`}>
                                        <RefreshCw size={14} />
                                    </Button>
                                </>
                            )}
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(k.id)}><Trash2 size={14} /></Button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Create app dialog */}
            <Dialog open={appOpen} onOpenChange={(o) => { setAppOpen(o); if (!o) setGenerated(null); }}>
                <DialogContent className="bg-slate-900 border-white/20 max-w-md">
                    <DialogHeader><DialogTitle className="text-white">{t('Cr\u00e9er une app (cl\u00e9 g\u00e9n\u00e9r\u00e9e automatiquement)', 'Create app (auto-generated key)')}</DialogTitle></DialogHeader>
                    {!generated ? (
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="text-white text-sm font-bold mb-2 block">{t('Nom de l\'application', 'Application name')}</Label>
                                <Input value={appName} onChange={e => setAppName(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleGenerateApp()}
                                    className="bg-slate-700 border-slate-500 text-white" placeholder="ex: sen-tontine"
                                    data-testid="app-name-input" />
                                <p className="text-xs text-slate-400 mt-2">
                                    {t('Une cl\u00e9 X-API-Key s\u00e9curis\u00e9e sera g\u00e9n\u00e9r\u00e9e et chiffr\u00e9e. L\'app sera aussi ajout\u00e9e aux Soldes avec une commission de 100% (modifiable).',
                                       'A secure X-API-Key will be generated and encrypted. The app will also appear in Balances with a default 100% commission (editable).')}
                                </p>
                            </div>
                            <Button onClick={handleGenerateApp} disabled={generating} className="w-full bg-emerald-600 hover:bg-emerald-500" data-testid="generate-app-key-btn">
                                <Key className="w-4 h-4 mr-1" /> {generating ? t('G\u00e9n\u00e9ration...', 'Generating...') : t('G\u00e9n\u00e9rer la cl\u00e9', 'Generate key')}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4" data-testid="generated-key-result">
                            <p className="text-sm text-slate-300">
                                {t('Cl\u00e9 cr\u00e9\u00e9e pour', 'Key created for')} <b className="text-emerald-400">{generated.app_name}</b> :
                            </p>
                            <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                                <code className="text-xs text-green-400 font-mono break-all flex-1" data-testid="generated-key-value">{generated.api_key}</code>
                                <Button size="sm" variant="ghost" onClick={() => copyKey(generated.api_key)} data-testid="copy-generated-key">
                                    <Copy size={14} />
                                </Button>
                            </div>
                            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                <span>{t('Copiez cette cl\u00e9 maintenant et transmettez-la au d\u00e9veloppeur de l\'app. Elle reste r\u00e9cup\u00e9rable via l\'\u0153il (mot de passe admin requis).',
                                         'Copy this key now and share it with the app developer. It remains recoverable via the eye icon (admin password required).')}</span>
                            </div>
                            <Button onClick={() => downloadIntegrationFile(generated.id)} variant="outline" className="w-full border-emerald-500/50 text-emerald-400 hover:text-emerald-300" data-testid="download-integration-btn">
                                <FileText className="w-4 h-4 mr-1" /> {t('T\u00e9l\u00e9charger le fichier d\'int\u00e9gration (prompt pr\u00eat \u00e0 l\'emploi)', 'Download integration file (ready-to-use prompt)')}
                            </Button>
                            <Button onClick={() => { setAppOpen(false); setGenerated(null); }} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="close-generated-dialog">
                                {t('Termin\u00e9', 'Done')}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add key dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="bg-slate-900 border-white/20 max-w-md">
                    <DialogHeader><DialogTitle className="text-white">{t('Ajouter une cl\u00e9 API', 'Add API key')}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-white text-sm font-bold mb-2 block">{t('Fournisseur', 'Provider')}</Label>
                            <Select value={newKey.provider} onValueChange={v => setNewKey(p => ({...p, provider: v}))}>
                                <SelectTrigger className="bg-slate-700 border-slate-500 text-white"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-slate-700 border-slate-500">
                                    <SelectItem value="wave" className="text-white">Wave</SelectItem>
                                    <SelectItem value="orange_money" className="text-white">Orange Money</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-white text-sm font-bold mb-2 block">{t('Nom de la cl\u00e9', 'Key name')}</Label>
                            <Input value={newKey.key_name} onChange={e => setNewKey(p => ({...p, key_name: e.target.value}))}
                                className="bg-slate-700 border-slate-500 text-white" placeholder="api_key, signing_secret, client_id..." />
                        </div>
                        <div>
                            <Label className="text-white text-sm font-bold mb-2 block">{t('Valeur', 'Value')}</Label>
                            <Input type="password" value={newKey.value} onChange={e => setNewKey(p => ({...p, value: e.target.value}))}
                                className="bg-slate-700 border-slate-500 text-white" placeholder="wave_sn_prod_..." />
                        </div>
                        <Button onClick={handleAdd} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="save-key-btn">
                            {t('Sauvegarder (chiffr\u00e9)', 'Save (encrypted)')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Password reveal dialog */}
            <Dialog open={passwordDialog.open} onOpenChange={() => { setPasswordDialog({ open: false, keyId: null }); setPassword(''); }}>
                <DialogContent className="bg-slate-900 border-white/20 max-w-sm">
                    <DialogHeader><DialogTitle className="text-white">{t('Mot de passe admin', 'Admin password')}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                            className="bg-slate-700 border-slate-500 text-white" placeholder="..."
                            onKeyDown={e => { if (e.key === 'Enter') handleReveal(); }}
                            data-testid="reveal-password-input" />
                        <Button onClick={handleReveal} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="reveal-confirm-btn">
                            {t('D\u00e9chiffrer', 'Decrypt')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// ==================== API DOCS ====================
const ApiDocs = () => {
    const { t } = useLanguage();
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const [copied, setCopied] = useState(null);

    const copy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    const downloadPostman = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            const res = await fetch(`${baseUrl}/api/payments/postman-collection`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                toast.error(t('Erreur t\u00e9l\u00e9chargement', 'Download error'));
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'dkr_payment_proxy_postman.json';
            a.click();
            URL.revokeObjectURL(url);
            toast.success(t('Collection Postman t\u00e9l\u00e9charg\u00e9e', 'Postman collection downloaded'));
        } catch (e) {
            toast.error(String(e));
        }
    };

    const endpoints = [
        {
            title: 'Wave - Checkout (create session)',
            method: 'POST',
            path: '/api/payments/wave/checkout',
            desc: t('Cr\u00e9er une session de paiement Wave.', 'Create a Wave checkout session.'),
            body: JSON.stringify({ amount: "1000", currency: "XOF", app_name: "togalma", success_url: "https://dkr-consulting.com/payment/success", error_url: "https://dkr-consulting.com/payment/error", client_reference: "CMD-001" }, null, 2),
            response: '{\n  "payment_id": "uuid",\n  "wave_launch_url": "https://pay.wave.com/c/...",\n  "checkout_status": "open"\n}',
            curl: `curl -X POST ${baseUrl}/api/payments/wave/checkout \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"1000","currency":"XOF","app_name":"togalma","success_url":"https://dkr-consulting.com/payment/success","error_url":"https://dkr-consulting.com/payment/error"}'`
        },
        {
            title: 'Wave - Get Checkout Session',
            method: 'GET',
            path: '/api/payments/wave/checkout/{session_id}',
            desc: t('R\u00e9cup\u00e9rer une session de paiement Wave par son ID.', 'Retrieve a Wave checkout session by ID.'),
            body: null,
            response: '{ "id": "cs_xxx", "checkout_status": "complete", ... }',
            curl: `curl -X GET ${baseUrl}/api/payments/wave/checkout/cs_xxx -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Wave - List Checkouts',
            method: 'GET',
            path: '/api/payments/wave/checkout?limit=25',
            desc: t('Lister les sessions de checkout Wave.', 'List Wave checkout sessions.'),
            body: null,
            response: '{ "items": [...], "page_info": {...} }',
            curl: `curl -X GET "${baseUrl}/api/payments/wave/checkout?limit=25" -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Wave - Refund Checkout',
            method: 'POST',
            path: '/api/payments/wave/refund',
            desc: t('Rembourser une session de paiement Wave.', 'Refund a Wave checkout session.'),
            body: JSON.stringify({ checkout_session_id: "cs_xxx", app_name: "togalma" }, null, 2),
            response: '{ "refunded": true, "checkout_session_id": "cs_xxx" }',
            curl: `curl -X POST ${baseUrl}/api/payments/wave/refund \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"checkout_session_id":"cs_xxx","app_name":"togalma"}'`
        },
        {
            title: 'Wave - Payout B2C (envoyer argent au client)',
            method: 'POST',
            path: '/api/payments/wave/payout',
            desc: t('Envoyer de l\'argent \u00e0 un client (disbursement).', 'Send money to a customer (B2C disbursement).'),
            body: JSON.stringify({ amount: "500", currency: "XOF", app_name: "togalma", mobile: "+221770000000", name: "Mamadou Sow", client_reference: "PAYOUT-001", payment_reason: "Reversement" }, null, 2),
            response: '{\n  "payment_id": "uuid",\n  "payout_id": "p_xxx",\n  "status": "processing"\n}',
            curl: `curl -X POST ${baseUrl}/api/payments/wave/payout \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"500","currency":"XOF","app_name":"togalma","mobile":"+221770000000","name":"Mamadou Sow","payment_reason":"Reversement"}'`
        },
        {
            title: 'Wave - Get Payout',
            method: 'GET',
            path: '/api/payments/wave/payout/{payout_id}',
            desc: t('R\u00e9cup\u00e9rer un payout Wave par son ID.', 'Retrieve a Wave payout by ID.'),
            body: null,
            response: '{ "id": "p_xxx", "status": "succeeded", ... }',
            curl: `curl -X GET ${baseUrl}/api/payments/wave/payout/p_xxx -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Wave - Balance compte marchand',
            method: 'GET',
            path: '/api/payments/wave/balance',
            desc: t('Solde du compte marchand Wave.', 'Wave merchant account balance.'),
            body: null,
            response: '{ "account_balances": [{"currency":"XOF","amount":"12345"}] }',
            curl: `curl -X GET ${baseUrl}/api/payments/wave/balance -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Wave - Transactions',
            method: 'GET',
            path: '/api/payments/wave/transactions?first=50',
            desc: t('Liste des transactions Wave (filtrable par date).', 'List Wave transactions (filterable by date).'),
            body: null,
            response: '{ "transactions": [...], "page_info": {...} }',
            curl: `curl -X GET "${baseUrl}/api/payments/wave/transactions?first=50&date=2026-06-01" -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Orange Money - Checkout (Web Payment)',
            method: 'POST',
            path: '/api/payments/orange/checkout',
            desc: t('Initier un paiement Orange Money web.', 'Initiate an Orange Money web payment.'),
            body: JSON.stringify({ amount: "1000", currency: "OUV", app_name: "togalma", return_url: "https://dkr-consulting.com/payment/success", cancel_url: "https://dkr-consulting.com/payment/error", notif_url: "https://dkr-consulting.com/api/payments/webhooks/orange", lang: "fr" }, null, 2),
            response: '{\n  "payment_id": "uuid",\n  "payment_url": "https://...",\n  "pay_token": "...",\n  "order_id": "ORDER-..."\n}',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/checkout \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"1000","currency":"OUV","app_name":"togalma","return_url":"https://dkr-consulting.com/payment/success","cancel_url":"https://dkr-consulting.com/payment/error","notif_url":"https://dkr-consulting.com/api/payments/webhooks/orange","lang":"fr"}'`
        },
        {
            title: 'Orange Money - QR Code',
            method: 'POST',
            path: '/api/payments/orange/qrcode',
            desc: t('G\u00e9n\u00e9rer un QR code Orange Money.', 'Generate an Orange Money QR code.'),
            body: JSON.stringify({ amount: "1000", currency: "OUV", app_name: "togalma", notif_url: "https://dkr-consulting.com/api/payments/webhooks/orange" }, null, 2),
            response: '{ "payment_id": "uuid", "qr_code": "data:image/png;base64,..." }',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/qrcode \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"1000","currency":"OUV","app_name":"togalma"}'`
        },
        {
            title: 'Orange Money - Cash-In (op\u00e9rateur \u2192 client)',
            method: 'POST',
            path: '/api/payments/orange/cashin',
            desc: t('Cr\u00e9diter le compte d\'un client.', 'Credit a customer wallet.'),
            body: JSON.stringify({ amount: "500", currency: "OUV", app_name: "togalma", customer_msisdn: "221770000000", reference: "REF-CASHIN-001", description: "Recharge", notif_url: "https://dkr-consulting.com/api/payments/webhooks/orange" }, null, 2),
            response: '{ "payment_id": "uuid", "order_id": "CASHIN-...", "status": "pending" }',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/cashin \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"500","currency":"OUV","app_name":"togalma","customer_msisdn":"221770000000","reference":"REF-CASHIN-001"}'`
        },
        {
            title: 'Orange Money - Cash-Out (client \u2192 op\u00e9rateur)',
            method: 'POST',
            path: '/api/payments/orange/cashout',
            desc: t('D\u00e9biter le compte d\'un client.', 'Debit a customer wallet.'),
            body: JSON.stringify({ amount: "300", currency: "OUV", app_name: "togalma", customer_msisdn: "221770000000", reference: "REF-CASHOUT-001", description: "Retrait", notif_url: "https://dkr-consulting.com/api/payments/webhooks/orange" }, null, 2),
            response: '{ "payment_id": "uuid", "order_id": "CASHOUT-...", "status": "pending" }',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/cashout \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"300","currency":"OUV","app_name":"togalma","customer_msisdn":"221770000000","reference":"REF-CASHOUT-001"}'`
        },
        {
            title: 'Orange Money - Merchant Payment (push USSD/PIN)',
            method: 'POST',
            path: '/api/payments/orange/merchant-payment',
            desc: t('Demander le paiement \u00e0 un client via push USSD (validation PIN).', 'Request a payment from a customer via USSD push (PIN validation).'),
            body: JSON.stringify({ amount: "1500", currency: "OUV", app_name: "jaayma", subscriber_msisdn: "221770000000", reference: "REF-MP-001", description: "Achat boutique", notif_url: "https://dkr-consulting.com/api/payments/webhooks/orange" }, null, 2),
            response: '{ "payment_id": "uuid", "order_id": "MP-...", "status": "pending" }',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/merchant-payment \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":"1500","currency":"OUV","app_name":"jaayma","subscriber_msisdn":"221770000000","reference":"REF-MP-001"}'`
        },
        {
            title: 'Orange Money - Transaction status',
            method: 'GET',
            path: '/api/payments/orange/transaction-status?order_id=...',
            desc: t('Statut OM par order_id.', 'OM status by order_id.'),
            body: null,
            response: '{ "status": "SUCCESS|PENDING|FAILED", "txnid": "..." }',
            curl: `curl -X GET "${baseUrl}/api/payments/orange/transaction-status?order_id=ORDER-123" -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Orange Money - Balance',
            method: 'GET',
            path: '/api/payments/orange/balance',
            desc: t('Solde du compte marchand OM.', 'OM merchant account balance.'),
            body: null,
            response: '{ "balance": 1234500, "currency": "OUV" }',
            curl: `curl -X GET ${baseUrl}/api/payments/orange/balance -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Orange Money - History',
            method: 'GET',
            path: '/api/payments/orange/history',
            desc: t('Historique des transactions OM (paginated).', 'OM transactions history (paginated).'),
            body: null,
            response: '{ "items": [...], "total": 1234 }',
            curl: `curl -X GET "${baseUrl}/api/payments/orange/history?page=1&size=50" -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Orange Money - Cancel / Refund',
            method: 'POST',
            path: '/api/payments/orange/cancel',
            desc: t('Annuler ou rembourser une transaction OM.', 'Cancel or refund an OM transaction.'),
            body: JSON.stringify({ order_id: "ORDER-123", app_name: "togalma", reason: "Demande client" }, null, 2),
            response: '{ "cancelled": true, "order_id": "ORDER-123" }',
            curl: `curl -X POST ${baseUrl}/api/payments/orange/cancel \\\n  -H "X-API-Key: VOTRE_PROXY_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"order_id":"ORDER-123","app_name":"togalma","reason":"Demande client"}'`
        },
        {
            title: 'Orange Money - Customer info / KYC',
            method: 'GET',
            path: '/api/payments/orange/customer/{msisdn}',
            desc: t('R\u00e9cup\u00e9rer les infos d\'un client OM par num\u00e9ro.', 'Get OM customer info by phone number.'),
            body: null,
            response: '{ "msisdn": "221770000000", "name": "...", "status": "..." }',
            curl: `curl -X GET ${baseUrl}/api/payments/orange/customer/221770000000 -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Statut paiement (proxy)',
            method: 'GET',
            path: '/api/payments/{payment_id}/status',
            desc: t('Polling depuis l\'app mobile.', 'Polled from the mobile app.'),
            body: null,
            response: '{ "payment_id": "uuid", "status": "pending|completed|failed|cancelled", "provider": "wave|orange_money" }',
            curl: `curl -X GET "${baseUrl}/api/payments/PAYMENT_ID/status" -H "X-API-Key: VOTRE_PROXY_KEY"`
        },
        {
            title: 'Retour / Deeplink universel',
            method: 'GET',
            path: '/api/payments/return',
            desc: t('Redirection apr\u00e8s paiement (deeplink mobile, URL web, ou pages par d\u00e9faut auto-close).', 'Post-payment redirect (mobile deeplink, web URL, or default auto-close pages).'),
            body: null,
            response: 'HTTP 302 Location: <redirect_url>?status=...&order_id=...',
            curl: `# Mobile (deeplink)\ncurl -L "${baseUrl}/api/payments/return?status=success&redirect_url=togalma://payment&order_id=ABC"\n\n# Web custom\ncurl -L "${baseUrl}/api/payments/return?status=success&redirect_url=https://dkr-consulting.com/retour&order_id=ABC"\n\n# Defaut\ncurl -L "${baseUrl}/api/payments/return?status=success&order_id=ABC"`
        },
        {
            title: 'Webhook Wave',
            method: 'POST',
            path: '/api/payments/webhooks/wave',
            desc: t('\u00c0 configurer dans Wave Business > D\u00e9veloppeur > Webhooks.', 'Configure in Wave Business > Developer > Webhooks.'),
            body: null, response: null, curl: null
        },
        {
            title: 'Webhook Orange Money',
            method: 'POST',
            path: '/api/payments/webhooks/orange',
            desc: t('R\u00e9ception des notifications OM (notif_url dans chaque requ\u00eate).', 'Receives OM notifications (notif_url in each request).'),
            body: null, response: null, curl: null
        }
    ];

    const keyGuide = [
        { provider: 'Wave', keys: [
            { name: 'api_key', desc: t('Cl\u00e9 API Wave (wave_sn_prod_...)', 'Wave API Key (wave_sn_prod_...)'), where: 'Wave Business Portal > D\u00e9veloppeur', status: 'configured' },
            { name: 'signing_secret', desc: t('Secret de signature (wave_sn_AKS_...)', 'Signing secret (wave_sn_AKS_...)'), where: 'Wave Business Portal > D\u00e9veloppeur', status: 'configured' },
        ]},
        { provider: 'Orange Money', keys: [
            { name: 'client_id', desc: 'OAuth Client ID', where: 'developer.orange.com > Mon app', status: 'configured' },
            { name: 'client_secret', desc: 'OAuth Client Secret', where: 'developer.orange.com > Mon app', status: 'configured' },
            { name: 'merchant_key', desc: t('Cl\u00e9 marchande (optionnel)', 'Merchant key (optional)'), where: 'developer.orange.com > OM Webpay', status: null },
        ]},
    ];

    return (
        <div className="space-y-6">
            {/* Postman collection download */}
            <div className="glass-card p-5 flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-white">{t('Collection Postman', 'Postman collection')}</h3>
                    <p className="text-xs text-slate-400 mt-1">{t('T\u00e9l\u00e9chargement r\u00e9serv\u00e9 \u00e0 l\'admin (auth requise).', 'Admin-only download (auth required).')}</p>
                </div>
                <Button onClick={downloadPostman} className="bg-blue-600 hover:bg-blue-500" data-testid="download-postman-btn">
                    <Download size={14} className="mr-2" />{t('T\u00e9l\u00e9charger', 'Download')}
                </Button>
            </div>

            {/* Key configuration guide */}
            <div className="glass-card p-5">
                <h3 className="text-lg font-bold text-white mb-4">{t('Configuration des cl\u00e9s', 'Key configuration')}</h3>
                {keyGuide.map(g => (
                    <div key={g.provider} className="mb-4">
                        <p className="text-sm font-bold text-blue-400 mb-2">{g.provider}</p>
                        <div className="space-y-1">
                            {g.keys.map(k => (
                                <div key={k.name} className="flex items-center gap-2 text-xs">
                                    <code className="text-green-400 bg-slate-800 px-1.5 py-0.5 rounded shrink-0">{k.name}</code>
                                    <span className="text-slate-300">{k.desc}</span>
                                    <span className="text-slate-500 shrink-0">({k.where})</span>
                                    {k.status === 'configured' && <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold shrink-0">{t('Configur\u00e9', 'Configured')}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Endpoints */}
            {endpoints.map((ep, i) => (
                <div key={i} className="glass-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${ep.method === 'POST' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>{ep.method}</span>
                        <code className="text-sm text-white font-mono">{ep.path}</code>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{ep.desc}</p>

                    {ep.body && (
                        <div className="mb-3">
                            <p className="text-xs font-bold text-slate-300 mb-1">Body :</p>
                            <pre className="bg-slate-800 p-3 rounded text-xs text-green-400 font-mono overflow-x-auto">{ep.body}</pre>
                        </div>
                    )}
                    {ep.response && (
                        <div className="mb-3">
                            <p className="text-xs font-bold text-slate-300 mb-1">{t('R\u00e9ponse :', 'Response:')}</p>
                            <pre className="bg-slate-800 p-3 rounded text-xs text-cyan-400 font-mono overflow-x-auto">{ep.response}</pre>
                        </div>
                    )}
                    {ep.curl && (
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-bold text-slate-300">cURL :</p>
                                <button onClick={() => copy(ep.curl, i)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
                                    {copied === i ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                                    {copied === i ? t('Copi\u00e9', 'Copied') : t('Copier', 'Copy')}
                                </button>
                            </div>
                            <pre className="bg-slate-800 p-3 rounded text-xs text-amber-400 font-mono overflow-x-auto whitespace-pre-wrap">{ep.curl}</pre>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// ==================== API TEST SANDBOX ====================
const ApiTestSandbox = ({ auth }) => {
    const { t } = useLanguage();
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const [method, setMethod] = useState('POST');
    const [url, setUrl] = useState(`${baseUrl}/api/payments/wave/checkout`);
    const [headers, setHeaders] = useState('{"Content-Type": "application/json"}');
    const [body, setBody] = useState(JSON.stringify({ amount: "500", currency: "XOF", app_name: "TestApp", success_url: "https://example.com/ok", error_url: "https://example.com/err" }, null, 2));
    const [response, setResponse] = useState(null);
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        setSending(true);
        try {
            let parsedHeaders = {};
            try { parsedHeaders = JSON.parse(headers); } catch {}
            let parsedBody = null;
            try { parsedBody = JSON.parse(body); } catch {}

            const res = await axios.post(`${baseUrl}/api/payments/test-api`, {
                url, method, headers: parsedHeaders, body: parsedBody
            }, auth);
            setResponse(res.data);
        } catch (e) {
            setResponse({ error: e.message, status_code: 0 });
        } finally { setSending(false); }
    };

    return (
        <div className="space-y-4">
            <div className="glass-card p-5 space-y-4">
                <div className="flex gap-3">
                    <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger className="bg-slate-700 border-slate-500 text-white w-28"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-500">
                            <SelectItem value="GET" className="text-white">GET</SelectItem>
                            <SelectItem value="POST" className="text-white">POST</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input value={url} onChange={e => setUrl(e.target.value)}
                        className="bg-slate-700 border-slate-500 text-white font-mono text-sm flex-1" data-testid="test-url" />
                </div>

                <div>
                    <Label className="text-white text-xs font-bold mb-1 block">Headers (JSON)</Label>
                    <Textarea value={headers} onChange={e => setHeaders(e.target.value)}
                        className="bg-slate-700 border-slate-500 text-white font-mono text-xs" rows={2} />
                </div>

                {method === 'POST' && (
                    <div>
                        <Label className="text-white text-xs font-bold mb-1 block">Body (JSON)</Label>
                        <Textarea value={body} onChange={e => setBody(e.target.value)}
                            className="bg-slate-700 border-slate-500 text-white font-mono text-xs" rows={6} data-testid="test-body" />
                    </div>
                )}

                <Button onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-500" data-testid="test-send-btn">
                    <Send size={14} className="mr-2" />
                    {sending ? t('Envoi...', 'Sending...') : t('Envoyer', 'Send')}
                </Button>
            </div>

            {response && (
                <div className="glass-card p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            response.status_code >= 200 && response.status_code < 300 ? 'bg-green-500/20 text-green-400' :
                            response.status_code >= 400 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                            {response.status_code || 'ERR'}
                        </span>
                        <span className="text-xs text-slate-400">{t('R\u00e9ponse', 'Response')}</span>
                    </div>
                    <pre className="bg-slate-800 p-3 rounded text-xs text-green-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-80">
                        {JSON.stringify(response.response || response.error, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default PaymentsSection;
